import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import { getAuth, type Auth } from "firebase/auth";
import { getFirestore, type Firestore } from "firebase/firestore";
import { getStorage, type FirebaseStorage } from "firebase/storage";

/**
 * Lê variáveis de ambiente (Next.js expostas no client começam com NEXT_PUBLIC_)
 */
const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
};

/**
 * Flags de ambiente
 */
const isBrowser = typeof window !== "undefined";
const isTest =
  typeof process !== "undefined" &&
  (process.env.NODE_ENV === "test" ||
    process.env.VITEST === "true" ||
    process.env.JEST_WORKER_ID !== undefined);

/**
 * Verifica se config está minimamente preenchida (apiKey + projectId)
 */
const hasMinimalConfig =
  !!firebaseConfig.apiKey &&
  !!firebaseConfig.projectId &&
  // Evita strings "undefined" / "null"
  firebaseConfig.apiKey !== "undefined" &&
  firebaseConfig.projectId !== "undefined";

/**
 * Armazena instâncias (podem ser null em testes ou sem config)
 */
let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let firestore: Firestore | null = null;
let storage: FirebaseStorage | null = null;

/**
 * Inicialização segura (idempotente). Não executa em testes ou sem config.
 */
function ensureInitialized() {
  if (app || isTest || !hasMinimalConfig) return;

  try {
    app = getApps().length ? getApps()[0]! : initializeApp(firebaseConfig);

    // Auth apenas no browser
    if (isBrowser) {
      try {
        auth = getAuth(app);
      } catch {
        auth = null;
      }
    }

    try {
      firestore = getFirestore(app);
    } catch {
      firestore = null;
    }

    try {
      storage = getStorage(app);
    } catch {
      storage = null;
    }
  } catch {
    // Se falhar, mantém tudo null
    app = null;
    auth = null;
    firestore = null;
    storage = null;
  }
}

/**
 * Getters lazy – chamam ensureInitialized() apenas quando necessário
 */
export function getFirebaseApp(): FirebaseApp | null {
  ensureInitialized();
  return app;
}

export function getFirebaseAuth(): Auth | null {
  ensureInitialized();
  return auth;
}

export function getFirebaseFirestore(): Firestore | null {
  ensureInitialized();
  return firestore;
}

export function getFirebaseStorage(): FirebaseStorage | null {
  ensureInitialized();
  return storage;
}

/**
 * Exports compatíveis com código legado que importava { app, auth, firestore, storage }.
 * Mantêm null em teste / sem config até que algum getter seja chamado explicitamente.
 * (Se quiser inicialização imediata fora de teste, descomente ensureInitialized(); abaixo)
 */
// ensureInitialized();
export { app, auth, firestore, storage };

/**
 * Opcional: helper para saber se Firebase está pronto
 */
export function isFirebaseReady() {
  return !!app;
}